import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ShieldCheck,
  Info,
  CheckCircle2
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import PricingBreakdownModal from '../components/ui/PricingBreakdownModal';
import { cars } from '../data/cars';
import { publicService } from '../services/public.service';
import { renterService } from '../services/renter.service';
import { getDefaultDailyPrice, normalizePricingConfigurations, resolveAppliedPricing } from '../utils/pricing';
import { buildPublicFileUrl } from '../utils/fileUrl';

const formatCurrency = (amount) => `RF ${Math.round(amount || 0).toLocaleString()}`;
const getTodayDateValue = () => new Date().toISOString().split('T')[0];
const getRentalDays = (pickupDate, returnDate) => {
  if (!pickupDate || !returnDate) return 0;
  const start = new Date(pickupDate);
  const end = new Date(returnDate);
  const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  return diff >= 0 ? Math.max(1, diff) : 0;
};
const formatDisplayDate = (value) => {
  if (!value) return 'Select date';
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const buildImageUrl = (imageUrl) => {
  if (!imageUrl) return 'https://images.unsplash.com/photo-1555215695-3004980ad54e?auto=format&fit=crop&q=80&w=1200';
  return buildPublicFileUrl(imageUrl);
};

const mapApiCarToViewModel = (car) => ({
  id: car.id,
  name: `${car.make || 'Premium'} ${car.model || 'Vehicle'}`.trim(),
  year: car.year,
  price: getDefaultDailyPrice(car.pricingConfigurations, car.pricePerDay || 0),
  image: buildImageUrl(car.primaryImageUrl),
  category: car.categoryName || 'Premium',
  cityName: car.cityName,
  allowOutsideCity: Boolean(car.allowOutsideCity),
  outsideCityPrice: car.outsideCityPrice || 0,
  serviceFee: Number(car.serviceFee || 0),
  longTermDiscount: car.longTermDiscount || 0,
  pricingConfigurations: normalizePricingConfigurations(car.pricingConfigurations || [])
});

const InitiatePayment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPricingBreakdown, setShowPricingBreakdown] = useState(false);
  const editingBookingId = location.state?.bookingId || '';
  const [bookingDetails, setBookingDetails] = useState({
    pickupCityId: location.state?.bookingDetails?.pickupCityId || '',
    destinationLocation: location.state?.bookingDetails?.destinationLocation || '',
    pickupDate: location.state?.bookingDetails?.pickupDate || '',
    returnDate: location.state?.bookingDetails?.returnDate || '',
    outsideCity: Boolean(location.state?.bookingDetails?.outsideCity)
  });

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await publicService.getCities();
        setCities(response);
      } catch (fetchError) {
        setCities([]);
      }
    };

    fetchCities();
  }, []);

  useEffect(() => {
    const fetchCar = async () => {
      const numericId = Number(id);
      const selectedCar = Number.isInteger(numericId) ? cars.find(c => c.id === numericId) : null;

      if (selectedCar) {
        setCar(selectedCar);
        setLoading(false);
        return;
      }

      try {
        const apiCar = await publicService.getCarById(id);
        setCar(mapApiCarToViewModel(apiCar));
      } catch (error) {
        navigate('/browse', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    fetchCar();
  }, [id, navigate]);

  useEffect(() => {
    if (!bookingDetails.pickupCityId && cities.length > 0) {
      const matchingCity = cities.find((city) => city.name === car?.cityName);
      setBookingDetails((current) => ({
        ...current,
        pickupCityId: matchingCity?.id || cities[0].id,
        destinationLocation: current.destinationLocation || ''
      }));
    }
  }, [cities, bookingDetails.pickupCityId, car]);

  if (loading || !car) return null;

  const rentalDays = getRentalDays(bookingDetails.pickupDate, bookingDetails.returnDate);
  const { appliedConfig, basePrice, breakdown } = resolveAppliedPricing(car.pricingConfigurations, rentalDays, car.price || 0);
  const serviceFee = Number(car.serviceFee || 0);
  const outsideCityFee = bookingDetails.outsideCity ? (car.outsideCityPrice || 0) : 0;
  const longTermDiscount = 0;
  const totalPrice = basePrice + outsideCityFee + serviceFee - longTermDiscount;
  const todayDateValue = getTodayDateValue();

  const validateBookingDates = () => {
    if (!bookingDetails.pickupDate || !bookingDetails.returnDate) {
      return 'Please select your destination location and rental dates before confirming.';
    }

    if (bookingDetails.pickupDate < todayDateValue) {
      return 'Pickup date cannot be before today.';
    }

    if (bookingDetails.returnDate < bookingDetails.pickupDate) {
      return 'Return date cannot be before pickup date.';
    }

    return '';
  };

  const handleCreateBooking = async () => {
    const resolvedCityId = bookingDetails.pickupCityId;

    const dateValidationError = validateBookingDates();
    if (!bookingDetails.destinationLocation.trim()) {
      setError('Please type your destination location before confirming.');
      return;
    }

    if (!resolvedCityId || dateValidationError) {
      setError(dateValidationError || 'Please select your destination location and rental dates before confirming.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await renterService.createBooking({
        bookingId: editingBookingId || undefined,
        carId: car.id,
        pickupCityId: resolvedCityId,
        pickupDate: bookingDetails.pickupDate,
        returnDate: bookingDetails.returnDate,
        outsideCity: bookingDetails.outsideCity,
        destinationLocation: bookingDetails.destinationLocation.trim(),
       paymentMethod: 'CARD'
      });

      setSuccessMessage(response.message || 'Booking created successfully.');
      navigate('/bookings', { replace: true });
    } catch (submitError) {
      setError(submitError.message || 'Failed to create booking.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col font-sans text-gray-900">
      <Navbar />

      <main className="flex-grow pt-8 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Progress Stepper */}
          <div className="mb-12">
            <div className="flex items-center gap-3 text-[10px] font-black tracking-[0.2em] text-[#FF6B00] mb-4">
               <span>RESERVE → PAY → TRAVEL</span>
               <div className="h-px w-12 bg-brand-orange-100"></div>
               <span className="text-gray-400">REVIEW RESERVATION</span>
            </div>
            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
               <div className="w-full h-full bg-[#FF6B00]"></div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-12 items-start">

            {/* Left Column: Reservation Review (60%) */}
            <div className="lg:w-3/5 space-y-8">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                <div className="flex items-start justify-between gap-6 mb-6">
                  <div>
                    <h2 className="text-xl font-black uppercase tracking-tight">Reserve This Car</h2>
                    <p className="text-sm text-gray-500 mt-2">
                      {editingBookingId
                        ? 'Update your reservation details below. Payment can be completed later from My Bookings.'
                        : 'This step saves your reservation now. Payment can be completed later from My Bookings.'}
                    </p>
                  </div>
                  <div className="rounded-xl bg-brand-orange-50 px-4 py-3 text-right">
                    <p className="text-[10px] font-black uppercase tracking-widest text-brand-black mb-1">Payment window</p>
                    <p className="text-sm font-black text-brand-black">1 Hour</p>
                  </div>
                </div>

                <div className="rounded-2xl border border-brand-orange-100 bg-brand-orange-50 px-5 py-4 text-sm text-brand-black leading-6">
                  Your booking will be saved immediately after you confirm below. If payment is not completed within 1 hour, the reservation will be cancelled automatically.
                </div>
              </div>

              {/* Booking Details Card */}
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                <h2 className="text-xl font-black uppercase tracking-tight mb-8">Booking Details</h2>
                <div className="space-y-6">
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm font-medium">
                      {error}
                    </div>
                  )}
                  {successMessage && (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm font-medium">
                      {successMessage}
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-bold text-gray-500 mb-2 block uppercase tracking-wider">Destination Location</label>
                    <input
                      maxLength={255}
                      value={bookingDetails.destinationLocation}
                      onChange={(e) => setBookingDetails((current) => ({
                        ...current,
                        destinationLocation: e.target.value
                      }))}
                      placeholder="Type destination location"
                      className="w-full px-5 py-3.5 bg-gray-50 border border-transparent rounded-xl text-sm font-semibold focus:bg-white focus:border-gray-200 outline-none transition-all"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs font-bold text-gray-500 mb-2 block uppercase tracking-wider">Pickup Date</label>
                      <input
                        type="date"
                        min={todayDateValue}
                        value={bookingDetails.pickupDate}
                        onChange={(e) => setBookingDetails((current) => ({
                          ...current,
                          pickupDate: e.target.value,
                          returnDate: current.returnDate && current.returnDate < e.target.value ? '' : current.returnDate
                        }))}
                        className="w-full px-5 py-3.5 bg-gray-50 border border-transparent rounded-xl text-sm font-semibold focus:bg-white focus:border-gray-200 outline-none transition-all placeholder:text-gray-300"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-500 mb-2 block uppercase tracking-wider">Return Date</label>
                      <input
                        type="date"
                        min={bookingDetails.pickupDate || todayDateValue}
                        value={bookingDetails.returnDate}
                        onChange={(e) => setBookingDetails((current) => ({ ...current, returnDate: e.target.value }))}
                        className="w-full px-5 py-3.5 bg-gray-50 border border-transparent rounded-xl text-sm font-semibold focus:bg-white focus:border-gray-200 outline-none transition-all placeholder:text-gray-300"
                      />
                    </div>
                  </div>
                  <label className="flex items-center justify-between bg-gray-50 rounded-xl px-5 py-4">
                    <div>
                      <p className="text-sm font-bold text-gray-900">Outside City Trip</p>
                      <p className="text-xs text-gray-500">
                        {car.allowOutsideCity ? `Adds ${formatCurrency(car.outsideCityPrice)}` : 'Not available for this destination setup'}
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={bookingDetails.outsideCity}
                      disabled={!car.allowOutsideCity}
                      onChange={(e) => setBookingDetails((current) => ({ ...current, outsideCity: e.target.checked }))}
                      className="w-4 h-4"
                    />
                  </label>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap items-center justify-center gap-8 pt-4">
                 <div className="flex items-center gap-2 text-gray-400">
                    <ShieldCheck className="w-5 h-5" />
                    <span className="text-[10px] font-black uppercase tracking-widest">Reservation Protected</span>
                 </div>
                 <div className="flex items-center gap-2 text-gray-400">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="text-[10px] font-black uppercase tracking-widest">Email Confirmation</span>
                 </div>
                 <div className="flex items-center gap-2 text-gray-400">
                    <Info className="w-5 h-5" />
                    <span className="text-[10px] font-black uppercase tracking-widest">Pay Later From Bookings</span>
                 </div>
              </div>

            </div>

            {/* Right Column: Order Summary (40%) */}
            <div className="lg:w-2/5 w-full">
              <div className="sticky top-28 space-y-6">

                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                  {/* Car Preview Header */}
                  <div className="bg-gray-50 p-6 flex gap-6 items-center">
                    <div className="w-32 aspect-video bg-white rounded-lg flex items-center justify-center p-2 border border-brand-orange-50">
                       <img src={car.image} alt={car.name} className="w-full h-auto object-contain mix-blend-multiply" />
                    </div>
                    <div>
                      <span className="text-[9px] font-black text-brand-black uppercase tracking-widest block mb-1">PREMIUM SELECTION</span>
                      <h3 className="text-lg font-black leading-tight">{car.name}</h3>
                      <p className="text-xs text-gray-400 font-bold">{car.year} • {car.category}</p>
                    </div>
                  </div>

                  <div className="p-8">
                    {/* Rental Schedule */}
                    <div className="grid grid-cols-2 gap-8 mb-8 pb-8 border-b border-gray-50">
                      <div>
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-3">Destination</p>
                        <p className="text-sm font-bold mb-1">{bookingDetails.destinationLocation || 'Type destination'}</p>
                        <p className="text-xs text-gray-500">{formatDisplayDate(bookingDetails.pickupDate)}</p>
                      </div>
                      <div>
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-3">Return</p>
                        <p className="text-sm font-bold mb-1">{bookingDetails.destinationLocation || 'Type destination'}</p>
                        <p className="text-xs text-gray-500">{formatDisplayDate(bookingDetails.returnDate)}</p>
                      </div>
                    </div>

                    {/* Price Breakdown */}
                    <div className="space-y-4 mb-8">
                       <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-500 font-medium">Rental Rate ({rentalDays || 0} days)</span>
                          <span className="font-bold">{formatCurrency(basePrice)}</span>
                       </div>
                       <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-500 font-medium">Applied Pricing Tier</span>
                          <span className="font-bold text-gray-900">{appliedConfig?.label || '1 Day'}</span>
                       </div>
                       <button
                         type="button"
                         onClick={() => setShowPricingBreakdown(true)}
                         className="text-left text-xs font-black uppercase tracking-widest text-brand-black hover:underline"
                       >
                         View Calculation
                       </button>
                       {outsideCityFee > 0 && (
                         <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-500 font-medium">Outside City Fee</span>
                            <span className="font-bold text-gray-900">{formatCurrency(outsideCityFee)}</span>
                         </div>
                       )}
                       {serviceFee > 0 && (
                         <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-500 font-medium">Service Fee</span>
                            <span className="font-bold text-gray-900">{formatCurrency(serviceFee)}</span>
                         </div>
                       )}
                       <div className="pt-4 border-t border-gray-100 flex justify-between items-center">
                          <span className="text-lg font-black text-gray-900">Reservation Total</span>
                          <span className="text-3xl font-black text-gray-900">{formatCurrency(totalPrice)}</span>
                       </div>
                    </div>

                    <p className="text-xs text-gray-500 mb-4">This total covers the selected car rental and listed fees. A separately booked driver has a separate agreed price and payment. Availability and the final amount are confirmed when your reservation is saved.</p>
                    {!!car.pricingConfigurations?.length && (
                      <div className="mb-8 rounded-xl border border-gray-100 bg-gray-50 p-4">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">All pricing configurations</p>
                        <div className="space-y-2">
                          {car.pricingConfigurations.map((item) => (
                            <div key={item.pricingType} className="flex items-center justify-between rounded-xl bg-white border border-gray-100 px-4 py-3">
                              <div>
                                <p className="text-sm font-black text-gray-900">{item.label}</p>
                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1">{item.rangeLabel}</p>
                              </div>
                              <span className="text-sm font-black text-gray-900">
                                {formatCurrency(item.amount)}{item.perDay ? ' / day' : ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Cancellation Callout */}
                    <div className="bg-brand-orange-50 rounded-xl p-4 flex gap-4 items-start mb-8">
                       <CheckCircle2 className="w-5 h-5 text-brand-black shrink-0" />
                       <div>
                          <p className="text-xs font-black text-brand-black uppercase tracking-wide mb-1">Cancellation & payment</p>
                          <p className="text-[11px] text-brand-black leading-relaxed font-medium">You can cancel an unpaid reservation from My Bookings. For paid bookings, contact support to review cancellation and refund eligibility.</p>
                       </div>
                    </div>

                    <button
                      disabled={submitting}
                      className="w-full bg-brand-black text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors"
                      onClick={handleCreateBooking}
                    >
                      {submitting
                        ? (editingBookingId ? 'Updating Reservation...' : 'Placing Booking...')
                        : (editingBookingId ? 'Update Reservation' : `Place Booking ${formatCurrency(totalPrice)}`)}
                    </button>

                    <p className="text-center text-[10px] text-gray-400 font-bold mt-6 uppercase tracking-widest">
                       No card payment required at this step
                    </p>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </main>

      <Footer />
      <PricingBreakdownModal
        isOpen={showPricingBreakdown}
        onClose={() => setShowPricingBreakdown(false)}
        rentalDays={rentalDays}
        breakdown={breakdown}
        basePrice={basePrice}
        outsideCityFee={outsideCityFee}
        serviceFee={serviceFee}
        totalPrice={totalPrice}
      />
    </div>
  );
};

export default InitiatePayment;
